import dayjs from "dayjs";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import useFindOrderCaa from "@repo/shopifyV2/hooks/useFindOrderCaa";
import useOrderDetailCaa from "@repo/shopifyV2/hooks/useOrderDetailCaa";
import useOrderInfoCaa from "@repo/shopifyV2/hooks/useOrderInfoCaa";
import useOrderStatusCaa from "@repo/shopifyV2/hooks/useOrderStatusCaa";
import { formatOrderAddress, toMailingAddressCaa } from "@repo/shopifyV2/utils/address";
import { OrderStatusTrans, getTrackingInfoCaa } from "@repo/shopifyV2/utils/order";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import { caaRequest } from "@lib/caaClient";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

export default function OrderDetailJp() {
  const navigate = useNavigate();
  const { handle } = useParams();
  const { t } = useTranslation();

  const { caaToken, caaCustomer } = useCustomerStore();
  const { setMeta, openAccount, openToast, showFooter } = useLayoutStore();

  useEffect(() => {
    window.scrollTo(0, 0);
    showFooter(true);
    setMeta({ title: `Order Detail - ${DEFAULT_TITLE}` });
  }, []);

  useEffect(() => {
    if (!caaToken?.accessToken) {
      setTimeout(() => {
        sessionStorage.setItem("loginAfterUrl", `${URLS.ORDER}${handle}`);
        openAccount(true);
      }, 300);
    }
  }, [caaToken]);

  const { order, loading, orderNotFound } = useFindOrderCaa({ orderId: handle, request: caaRequest });

  const { completeOrder, returnOrder, refundOrder, totalReturnShippingFee, isLoading } = useOrderDetailCaa({
    lang: "jp",
    order,
    customerId: caaCustomer?.id,
    openToast,
  });

  const { orderStatus } = useOrderStatusCaa(order, { allowCancel: false, allowReturn: false });

  // 배송 송장 정보
  const trackingInfo = getTrackingInfoCaa(order);

  useEffect(() => {
    if (!loading && orderNotFound) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [loading, orderNotFound]);

  const shippingAddress = useMemo(() => formatOrderAddress({ address: toMailingAddressCaa(order?.shippingAddress), lang: "jp" }), [order]);
  const billingAddress = useMemo(() => formatOrderAddress({ address: toMailingAddressCaa(order?.billingAddress), lang: "jp" }), [order]);

  const {
    shippingAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    refundAmount,
    codeDiscountAmount,
    autoDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    shippingTitle,
  } = useOrderInfoCaa(order, { taxesIncluded: true });

  return (
    <>
      {!order || loading || isLoading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
          <section className="pc:col-span-10 pc:flex-col col-span-8 flex justify-between gap-5">
            <section className="pc:text-[1.125rem] flex flex-col gap-1 text-lg leading-none">
              <h2>{order.name || ""}</h2>
              <div className="pc:flex-row pc:gap-2.5 flex flex-col gap-2">
                <span>{dayjs(order?.processedAt).format("MMM DD, YYYY")}</span>
                <div className="flex gap-2.5">
                  <span className="text-md pc:text-lg leading-none">{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
                  {trackingInfo ? (
                    <Link as="text" className="pc:text-lg! w-fit leading-none font-normal underline" href={trackingInfo.url ?? ""}>
                      {trackingInfo.number}
                    </Link>
                  ) : null}
                </div>
              </div>
            </section>
          </section>

          <section className="pc:col-span-10 pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-10">
            <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-5 leading-none">
              {shippingTitle ? (
                <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                  <span>Shipping Method</span>
                  <span className="font-normal">{shippingTitle}</span>
                </div>
              ) : null}

              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span>Shipping Address</span>
                <Address {...shippingAddress} />
              </div>

              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span>Billing Address</span>
                <Address {...billingAddress} />
              </div>
            </section>

            <section className="pc:col-span-5 pc:col-start-4 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10 leading-none">
              {/* 반품 내역 - (returnRequested, returnApproved) */}
              {returnOrder.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Return Items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {returnOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* 주문 진행 내역 - (unfulfilled, fulfilled, onhold) */}
              {completeOrder.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Line items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {completeOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* 환불 내역 - (canceled, refunded)*/}
              {refundOrder.length > 0 ? (
                <div className="tnt-gap-x col-span-8 flex flex-col gap-y-5">
                  <span className="pc:col-span-5 col-span-8">Refunded Items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {refundOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="pc:items-end pc:col-start-4 col-span-8 flex w-full flex-col">
                <div className="pc:w-screen-col-span-2 flex flex-col gap-1">
                  <OrderPaymentInfo
                    autoDiscountAmount={autoDiscountAmount}
                    autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                    codeDiscountAmount={codeDiscountAmount}
                    codeDiscountTitle={codeDiscountTitle}
                    dutyAmount={dutyAmount}
                    originalTotalAmount={subTotalAmount}
                    refundAmount={refundAmount}
                    returnShippingFee={totalReturnShippingFee}
                    shippingFee={shippingAmount}
                    totalAmount={totalAmount}
                  />
                </div>
              </div>
            </section>
          </section>
        </section>
      )}
    </>
  );
}
